import { CmsProduct } from '../../../../types/cmsProduct';
import { PushDataLayerFn } from '../../../../types/pushDataLayer';
import {
  ComponentTypeEnum,
  sendProductAnalytics,
  UserActionEnum,
} from '../../../../utils/analytics';

type Viewport = 'desktop' | 'mobile';

export type NavInteraction = {
  // Desktop renders tabs, mobile a dropdown — this decides component_type/action
  // for every nav, independent of its event payload.
  viewport: Viewport;
  // Label of the clicked tab / selected option; also the component_title suffix.
  label: string;
  // Position of the item among the real nav items (spacers excluded).
  index: number;
  // Only the default event carries product data. The TI event is product-free, so
  // it also fires before a tier has been selected.
  product?: CmsProduct;
  // Default event only — the TI event carries no item_list_name (not in its spec).
  itemListName?: string;
  pushDataLayer: PushDataLayerFn;
};

/**
 * Pushes the Sticky Navigation Bar interaction event. Each nav passes its own one to the desktop
 * and mobile bars, so both viewports of a nav stay in parity.
 */
export type PushNavInteraction = (interaction: NavInteraction) => void;

const viewportEvent = (viewport: Viewport) =>
  viewport === 'mobile'
    ? { componentType: ComponentTypeEnum.DROPDOWN, action: UserActionEnum.SELECT }
    : { componentType: ComponentTypeEnum.TAB, action: UserActionEnum.CLICK };

/**
 * TI PDP (CMS-1861 Event 2) → `navigate` / `Module Card Details_*`, a flat push of exactly the five
 * fields in the spec. No `items[]`, `reward_campaign_*` or `product_context_parameter`: the spec
 * marks those Event-3-only, and analytics QA asked for the product payload to be dropped from the
 * navigation events.
 */
export const pushNavInteractionTi: PushNavInteraction = ({
  viewport,
  label,
  index,
  pushDataLayer,
}) => {
  const { componentType, action } = viewportEvent(viewport);
  pushDataLayer({
    event: 'navigate',
    component_type: componentType,
    component_title: `Module Card Details_${label}`,
    action,
    index,
  });
};

/**
 * Default PDP (CC / PL, CMS-1784) → unchanged `product_interaction` through `sendProductAnalytics`:
 * `Sticky Navigation Bar_*` with the fixed items[] index of `'0'`. That is a shipped contract for the
 * other consumer of this library, so it keeps the full product payload.
 */
export const pushNavInteractionDefault: PushNavInteraction = ({
  viewport,
  label,
  product,
  itemListName,
  pushDataLayer,
}) => {
  // The event is built from product data, so without a product there is nothing to send.
  if (!product) return;

  const { componentType, action } = viewportEvent(viewport);
  sendProductAnalytics({
    event: 'product_interaction',
    action,
    componentType,
    componentTitle: `Sticky Navigation Bar_${label}`,
    itemListName,
    product,
    index: '0',
    pushDataLayer,
  });
};
